import { Mail } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { web, errMsg } from "../../lib/api";

const ZONES = [
  ["America/New_York", "Eastern"], ["America/Chicago", "Central"], ["America/Denver", "Mountain"], ["America/Phoenix", "Arizona"],
  ["America/Los_Angeles", "Pacific"], ["America/Anchorage", "Alaska"], ["Pacific/Honolulu", "Hawaii"], ["America/Puerto_Rico", "Atlantic"],
  ["America/Toronto", "Toronto"], ["Europe/London", "London"], ["Europe/Paris", "Central Europe"], ["Australia/Sydney", "Sydney"], ["UTC", "UTC"],
];
const hourLabel = (h) => `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`;
const browserZone = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } };
const sel = "rounded-full border border-[#1E293B] bg-[#162032] px-3.5 py-2 text-sm text-white disabled:opacity-50";

/** Daily summary email: on/off, delivery hour and time zone (saved per recruiter). */
export const EmailPrefs = () => {
  const { data, reload } = useApi("/recruiter/alerts/prefs", web);
  if (!data) return null;
  const prefs = { email_daily: data.email_daily !== false, hour: data.hour ?? 7, timezone: data.timezone ?? "America/New_York" };
  const local = browserZone();
  const zones = [...ZONES, ...(local && !ZONES.some(([z]) => z === local) ? [[local, local.replace(/_/g, " ")]] : []),
    ...(!ZONES.some(([z]) => z === prefs.timezone) && prefs.timezone !== local ? [[prefs.timezone, prefs.timezone]] : [])];
  const zoneLabel = zones.find(([z]) => z === prefs.timezone)?.[1] ?? prefs.timezone;

  const save = async (patch, msg) => {
    try {
      await web.put("/recruiter/alerts/prefs", { ...prefs, ...patch });
      toast.success(msg);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="alert-email-pref" className="mb-6 rounded-2xl border border-[#1E293B] bg-[#0F172A] px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-3 text-sm text-slate-200">
          <Mail className="h-4 w-4 shrink-0 text-[#39FF14]" />
          <span className="min-w-0">
            <span data-testid="alert-email-summary">{prefs.email_daily ? `Daily summary email at ${hourLabel(prefs.hour)} ${zoneLabel}` : "Daily summary email is off"}</span>
            {data.email ? <span className="block truncate text-xs text-slate-400">to {data.email}</span> : null}
          </span>
        </span>
        <button data-testid="alert-email-toggle" role="switch" aria-checked={prefs.email_daily} aria-label="Daily summary email"
          onClick={() => save({ email_daily: !prefs.email_daily }, prefs.email_daily ? "Daily emails turned off" : "Daily emails turned on")}
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${prefs.email_daily ? "bg-[#39FF14]" : "bg-[#162032]"}`}>
          <span className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-[#0A0E1A] transition-transform ${prefs.email_daily ? "translate-x-6" : "translate-x-1"}`} />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 pl-7">
        <label className="sr-only" htmlFor="alert-hour">Delivery time</label>
        <select id="alert-hour" data-testid="alert-hour-select" disabled={!prefs.email_daily} value={prefs.hour}
          onChange={(e) => save({ hour: Number(e.target.value) }, `Emails will arrive at ${hourLabel(Number(e.target.value))}`)} className={sel}>
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
        <label className="sr-only" htmlFor="alert-zone">Time zone</label>
        <select id="alert-zone" data-testid="alert-timezone-select" disabled={!prefs.email_daily} value={prefs.timezone}
          onChange={(e) => save({ timezone: e.target.value }, "Time zone saved")} className={sel}>
          {zones.map(([z, l]) => <option key={z} value={z}>{l}{z === local ? " (your device)" : ""}</option>)}
        </select>
      </div>
    </div>
  );
};
